import clsx from 'clsx';
import type { MouseEvent, ReactNode } from 'react';

export interface ButtonProps {
  icon?: ReactNode;
  text?: string;
  onClick: () => void;
  disabled?: boolean;
  className?: string;
  variant?: 'filled' | 'outlined' | 'text';
  size?: 'small' | 'medium' | 'large';
  type?: 'button' | 'submit' | 'reset';
  confirm?: string;
  color?: 'primary' | 'secondary' | 'create' | 'delete';
  /** Tooltip, typically used to explain why the button is disabled */
  title?: string;
  'aria-label'?: string;
}

function variantClasses(variant: NonNullable<ButtonProps['variant']>, color: NonNullable<ButtonProps['color']>) {
  /* TODO: uses temporary styles from old project, redesigned as site with color palette is designed */
  const palette = {
    primary: {
      filled:
        // `frosted-blue` isn't a theme token, so this background is transparent; text must stay light
        'bg-frosted-blue text-pearl shadow-none hover:bg-frosted-blue/80 hover:-translate-y-0.5 hover:shadow-[0_12px_26px_rgba(144,221,240,0.38)] active:translate-y-0 active:shadow-none focus-visible:ring-frosted-blue',
      outlined: 'text-platinum border border-white/20 hover:bg-white/10 hover:border-white/30 focus-visible:ring-frosted-blue',
      text: 'text-platinum hover:text-frosted-blue hover:bg-white/10',
    },
    secondary: {
      filled:
        'bg-white/10 text-platinum shadow-none hover:bg-white/15 hover:-translate-y-0.5 hover:shadow-[0_12px_26px_rgba(255,255,255,0.12)] active:translate-y-0 active:shadow-none focus-visible:ring-frosted-blue',
      outlined: 'text-platinum border border-white/15 hover:bg-white/10 focus-visible:ring-frosted-blue',
      text: 'text-platinum/80 hover:text-platinum hover:bg-white/10',
    },
    create: {
      filled:
        'bg-emerald-400 text-void shadow-none hover:bg-emerald-300 hover:-translate-y-0.5 hover:shadow-[0_12px_26px_rgba(52,211,153,0.35)] active:translate-y-0 active:shadow-none focus-visible:ring-emerald-300',
      outlined:
        'text-emerald-100 border border-emerald-300/60 hover:bg-emerald-300/10 hover:border-emerald-300/70 focus-visible:ring-emerald-300',
      text: 'text-emerald-100 hover:text-emerald-50 hover:bg-emerald-300/10',
    },
    delete: {
      filled:
        'bg-red-500 text-white shadow-none hover:bg-red-500/90 hover:-translate-y-0.5 hover:shadow-[0_12px_26px_rgba(248,113,113,0.35)] active:translate-y-0 active:shadow-none focus-visible:ring-red-400',
      outlined: 'text-red-200 border border-red-400/60 hover:bg-red-500/10 focus-visible:ring-red-300',
      text: 'text-red-200 hover:text-red-100 hover:bg-red-500/10',
    },
  } as const;

  return palette[color][variant];
}

function sizeClasses(size: NonNullable<ButtonProps['size']>) {
  switch (size) {
    case 'small':
      return 'text-xs px-3 py-1';
    case 'large':
      return 'text-base px-5 py-3';
    case 'medium':
    default:
      return 'text-sm px-4 py-2';
  }
}

export default function Button({
  icon,
  text,
  onClick,
  disabled = false,
  className,
  variant = 'filled',
  size = 'medium',
  type = 'button',
  confirm,
  color = 'primary',
  title,
  'aria-label': ariaLabel,
}: ButtonProps) {
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (disabled) return;
    if (confirm && !window.confirm(confirm)) return;
    onClick();
  };

  const base =
    'inline-flex items-center justify-center gap-2 rounded-full transition duration-150 ease-out focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-60';

  return (
    <button
      type={type}
      onClick={handleClick}
      disabled={disabled}
      title={title}
      aria-label={ariaLabel ?? (text ? undefined : title)}
      className={clsx(base, variantClasses(variant, color), sizeClasses(size), className)}
    >
      {icon ? <span className="inline-flex h-4 w-4 items-center justify-center">{icon}</span> : null}
      {text}
    </button>
  );
}
